import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, takeUntil } from 'rxjs';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { CreateModuleRightsDirective } from '../../../core/auth/directives/can-create-module.directive';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatOption,
  MatOptgroup,
} from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { UtilityService } from '../../services/utility.service';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { FormlyFieldAutocomplete, AUTOCOMPLETE_TEMPLATE } from './autocomplete.component';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { FieldLabelRowComponent } from './field-label-row.component';

@Component({
  selector: 'formly-field-dynamic-module-autocomplete',
  template: AUTOCOMPLETE_TEMPLATE,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    // Shares AUTOCOMPLETE_TEMPLATE with autocomplete.component.ts, which renders
    // <app-field-label-row>. Without this import that element is unknown here and every
    // dynamic-module-autocomplete field fails to render (NG0304).
    FieldLabelRowComponent,
    MatIcon,
    MatIconButton,
    MatTooltip,
    CreateModuleRightsDirective,
    MatHint,
    MatAutocomplete,
    MatOption,
    MatOptgroup,
    MatSuffix,
    MatInput,
    MatLabel,
    MatFormField,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    FormlyErrorComponent,
    MatProgressSpinner,
    FormlyDataCyDirective,
    FuseAlertComponent,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
  styles: `
    ::ng-deep .mdc-list-item__primary-text {
      width: 100%;
      display: inline-block;
    }

    ::ng-deep .cdk-overlay-pane {
      min-width: 320px;
    }

    ::ng-deep .mat-mdc-optgroup-label .mdc-list-item__primary-text {
      font-weight: 500;
      span {
        font-size: 14px;
        font-weight: 400 !important;
      }
    }
  `,
})
export class FormlyFieldDynamicModuleAutocomplete
  extends FormlyFieldAutocomplete
  implements OnInit, OnDestroy
{
  readonly #modulesStore = inject(ModulesStore);

  override onlyManualOptions = computed(() => {
    if (this.linkedData().length > 0) return false;
    return this.field.props?.optionsArray === undefined;
  });

  override ngOnInit(): void {
    // Ensure requireSelection is defined so the input field renders
    // (AUTOCOMPLETE_TEMPLATE hides everything when requireSelection is undefined)
    if (this.field.props.requireSelection === undefined) {
      this.field.props.requireSelection = true;
    }

    this.currentValue.set(this.field.formControl.value);

    const moduleFieldKey = this.field.props?.moduleFieldKey;
    if (!moduleFieldKey) {
      super.ngOnInit();
      return;
    }

    // Extract the field key from the keyPath (e.g., 'entry.item-1.moduleId' → 'moduleId')
    const fieldKey = moduleFieldKey.includes('.') ? moduleFieldKey.split('.').pop() : moduleFieldKey;
    const siblingField = UtilityService.findClosestFieldByKey(this.field, fieldKey);
    if (!siblingField?.formControl) {
      super.ngOnInit();
      return;
    }

    this.#setupDynamicModuleResolution(siblingField);
    this.setupValueChanges();
  }

  #setupDynamicModuleResolution(siblingField: FormlyFieldConfig): void {
    const initialModuleId = siblingField.formControl.value;
    if (initialModuleId) {
      this.#resolveAndSetModule(initialModuleId);
    }

    siblingField.formControl.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(moduleId => {
        this.clearValue(false);
        this.filteredOptions.set([]);
        this.loaded.set(false);
        this.currentOffset.set(0);
        this.hasMore.set(true);

        if (!moduleId) {
          this.linkedData.set([]);
          return;
        }
        this.#resolveAndSetModule(moduleId);
      });
  }

  #resolveAndSetModule(moduleId: string): void {
    const module = this.#modulesStore.getModuleById(moduleId);
    if (!module) {
      this.linkedData.set([]);
      return;
    }

    this.linkedData.set([{
      moduleCode: module.code,
      moduleLinkedField: module.titelKeyPath ?? 'entry.selectedInput',
      moduleValueType: 'entry-small',
      module,
      filters: [],
      options: [],
      optionsType: 'module',
      taxonomyId: undefined,
    }]);

    const currentValue = this.field.formControl.value;
    if (currentValue) {
      this.isLoading.set(true);
      this.dynamicOptionsService
        .fetchOptionsForValue(undefined, this.linkedData(), '', this.field, 0, this.pageSize)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: options => {
            this.filteredOptions.set(options);
            this.updateSearchFieldValue();
            this.isLoading.set(false);
            this.loaded.set(true);
          },
          error: () => {
            this.isLoading.set(false);
            this.loaded.set(true);
          },
        });
    } else {
      this.loaded.set(true);
    }
  }
}
